import type { Finding } from '@/features/agentic-insights/components/data/types';

export interface FigureLine {
  metric: string;
  label: string;
  /** What the figure is not precise about, and what it was measured against — once each. */
  qualifiers: string[];
}

const normalise = (value: string) =>
  value
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;]$/, '')
    .trim();

const firstNumber = (value: string) => value.match(/\d[\d,.]*/)?.[0];

/**
 * The figure a claim turns on, as one line with nothing said twice.
 *
 * Four fields describe it — the number, what it counts, what it is not precise about, and what it
 * was measured against — and three of them are free text the agent wrote separately, so the same
 * base routinely arrives more than once: "1,339 open items with no estimate", "of 1,360 open items",
 * "Of 1,360 open items in loop 4". Rendered as written, the figure reads as a stutter and a reader
 * has to compare three numbers to notice that two of them are the same number.
 *
 * Two things go, and only these two:
 *
 * A qualifier or denominator already contained in the other, which is the case above — the longer
 * one carries everything the shorter one said, plus where it was measured.
 *
 * The denominator when the metric already states it: "1 of 1,363" alongside "Of 1,363 items in the
 * loop" is one figure and its own base, written twice. Compared on the leading number rather than on
 * the text, since the two are never phrased the same way.
 */
export const buildFigureLine = (
  finding: Pick<Finding, 'metric' | 'metricLabel' | 'metricQualifier' | 'denominator'>,
  denominatorPhrase: (denominator: string) => string,
): FigureLine => {
  const metricStatesDenominator = (denominator: string) => {
    const base = firstNumber(denominator);
    return base !== undefined && finding.metric.includes(base);
  };

  const candidates = [
    finding.metricQualifier,
    finding.denominator && !metricStatesDenominator(finding.denominator)
      ? denominatorPhrase(finding.denominator)
      : undefined,
  ].filter((value): value is string => Boolean(value));

  const qualifiers = candidates.filter((candidate, index) =>
    candidates.every((other, otherIndex) => {
      if (otherIndex === index) return true;
      const contained = normalise(other).includes(normalise(candidate));
      // When each contains the other they are the same sentence; the first one stated wins.
      if (contained && normalise(candidate).includes(normalise(other))) return otherIndex > index;
      return !contained;
    }),
  );

  return { metric: finding.metric, label: finding.metricLabel, qualifiers };
};
